import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import fullLogo from '../assets/full_logo.png';
import { Icon } from './ui';

export default function ClientSidebar({ onNavClick = () => {} }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { activeWorkspace } = useWorkspace();
  const [openFlyoutKey, setOpenFlyoutKey] = useState(null);
  const [flyoutTopPx, setFlyoutTopPx] = useState(0);
  const sidebarRef = useRef(null);
  const flyoutRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      const inSidebar = sidebarRef.current?.contains(e.target);
      const inFlyout = flyoutRef.current?.contains(e.target);
      if (!inSidebar && !inFlyout) setOpenFlyoutKey(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const menuItems = [
    { path: '/client/dashboard', label: 'Tableau de bord', icon: 'IconDashboard' },
    { path: '/client/settings', label: 'Parametres', icon: 'IconSettings' },
    {
      key: 'ventes',
      label: 'Ventes',
      icon: 'IconShoppingCart',
      children: [
        { path: '/client/clients', label: 'Clients', icon: 'IconUsers' },
        { path: '/client/quotes', label: 'Devis', icon: 'IconFileText' },
        { path: '/client/invoices', label: 'Factures', icon: 'IconFileInvoice' },
        { path: '/client/credit-notes', label: 'Avoirs', icon: 'IconArrowBackUp' },
        { path: '/client/payments', label: 'Reglements', icon: 'IconCreditCard' },
      ],
    },
    {
      key: 'achats',
      label: 'Achats',
      icon: 'IconShoppingBag',
      children: [
        { path: '/client/suppliers', label: 'Fournisseurs', icon: 'IconTruck' },
        { path: '/client/purchase-invoices', label: 'Factures', icon: 'IconFileInvoice' },
        { path: '/client/purchase-payments', label: 'Reglements', icon: 'IconMoneybag' },
        { path: '/client/purchase-payments/rs', label: 'Retenues RS', icon: 'IconPercentage' },
      ],
    },
    {
      key: 'tresorerie',
      label: 'Tresorerie',
      icon: 'IconWallet',
      children: [
        { path: '/client/treasury/cash', label: 'Caisse', icon: 'IconCash' },
        { path: '/client/treasury/bank', label: 'Banque', icon: 'IconBuildingBank' },
        { path: '/client/treasury/rs', label: 'Retenues RS', icon: 'IconPercentage' },
      ],
    },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openedGroup = menuItems.find((x) => x.key && x.key === openFlyoutKey) || null;

  const renderLinkItem = (item) => {
    const isActive = location.pathname === item.path;
    return (
      <li key={item.path} className="px-3">
        <Link
          to={item.path}
          onClick={() => { setOpenFlyoutKey(null); onNavClick(); }}
          className={`flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-all duration-200 ${
            isActive
              ? 'bg-gradient-to-r from-gray-700 to-gray-800 text-white shadow-md border-l-4 border-blue-500'
              : 'text-gray-300 hover:text-white hover:bg-gray-700'
          }`}
        >
          <Icon name={item.icon} className="w-5 text-center" />
          <span>{item.label}</span>
        </Link>
      </li>
    );
  };

  return (
    <nav
      ref={sidebarRef}
      className="w-72 bg-gray-800 text-white h-screen fixed z-50 flex flex-col shadow-2xl"
      style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
    >
      <style>{`.no-scrollbar::-webkit-scrollbar { display: none; }`}</style>

      {/* LOGO */}
      <div className="p-4 flex flex-col items-center justify-center border-b border-gray-700 bg-gray-900/50 flex-shrink-0">
        <div className="bg-white p-2 rounded-lg w-full flex justify-center mb-2 shadow-sm">
          <img src={fullLogo} alt="AccountInTime" className="h-10 w-auto object-contain" />
        </div>
        <p className="text-gray-400 text-xs mt-1 uppercase tracking-widest font-semibold">
          Espace Client
        </p>
      </div>

      {/* INFO UTILISATEUR */}
      <div className="p-4 bg-gray-700/50 mx-3 my-4 rounded-lg text-sm border border-gray-600/50 flex-shrink-0">
        <div className="flex justify-between items-center">
          <div>
            <strong className="block text-gray-100">{currentUser?.name}</strong>
            <span className="inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-bold text-white tracking-wider bg-blue-500">
              CLIENT
            </span>
            {activeWorkspace && (
              <p className="text-gray-400 text-xs mt-1 truncate max-w-[160px]">{activeWorkspace.name}</p>
            )}
          </div>
          <div className="h-2 w-2 bg-green-500 rounded-full shadow-[0_0_5px_2px_rgba(34,197,94,0.4)]" />
        </div>
      </div>

      {/* NAVIGATION */}
      <ul className="flex-1 py-2 space-y-1 overflow-y-auto no-scrollbar pb-6">
        {menuItems.map((item) => {
          if (!item.children) return renderLinkItem(item);

          const isGroupActive = item.children.some((c) => location.pathname === c.path);
          const isOpen = openFlyoutKey === item.key;

          return (
            <li key={item.key} className="px-3">
              <button
                type="button"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  setFlyoutTopPx(rect.top + window.scrollY);
                  setOpenFlyoutKey((k) => (k === item.key ? null : item.key));
                }}
                className={`w-full flex items-center justify-between gap-3 px-3 py-3 rounded-lg text-sm transition-all duration-200 ${
                  isGroupActive || isOpen
                    ? 'bg-gradient-to-r from-gray-700 to-gray-800 text-white shadow-md border-l-4 border-blue-500'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                }`}
              >
                <span className="flex items-center gap-3">
                  <i className={`${item.icon} w-5 text-center`} />
                  <span>{item.label}</span>
                </span>
                <Icon name="IconChevronRight" size={14} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
              </button>
              {isOpen && (
                <ul className="md:hidden ml-4 mt-1 space-y-1 border-l-2 border-gray-700 pl-2">
                  {item.children.map((child) => {
                    const isChildActive = location.pathname === child.path;
                    return (
                      <li key={child.path}>
                        <Link
                          to={child.path}
                          onClick={() => { setOpenFlyoutKey(null); onNavClick(); }}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                            isChildActive
                              ? 'text-white bg-gray-700'
                              : 'text-gray-400 hover:text-white hover:bg-gray-700'
                          }`}
                        >
                          <Icon name={child.icon} className="w-5 text-center" />
                          <span>{child.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>

      {/* FLYOUT (desktop only) */}
      {!!openedGroup && (
        <div
          ref={flyoutRef}
          className="hidden md:block absolute left-full z-50 w-64 bg-gray-900 border border-gray-700 shadow-2xl rounded-xl"
          style={{ top: flyoutTopPx }}
        >
          <div className="p-2 max-h-80 overflow-y-auto">
            <ul className="space-y-1">
              {openedGroup.children.map((child) => {
                const isActive = location.pathname === child.path;
                return (
                  <li key={child.path}>
                    <Link
                      to={child.path}
                      onClick={() => { setOpenFlyoutKey(null); onNavClick(); }}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                        isActive
                          ? 'bg-gray-800 text-white border-l-4 border-blue-500'
                          : 'text-gray-300 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      <Icon name={child.icon} className="w-5 text-center" />
                      <span>{child.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div className="mt-auto bg-gray-900/50 border-t border-gray-700 p-4 flex-shrink-0">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-400 hover:text-white hover:bg-red-600/20 border border-red-600/30 w-full text-left transition-all duration-200"
        >
          <Icon name="IconLogout" className="w-5 text-center" />
          <span>Deconnexion</span>
        </button>
      </div>
    </nav>
  );
}